import React, { useEffect, useState } from "react";
import "./adminPlayground.scss";
import _ from "lodash";
import PlaygroundHeader from "./PlaygroundHeader";
import OrganizationPrompt from "./OrganizationPrompt";
import TestSessions from "./TestSessions";
import virtualLibrarianSettingsStore from "../../../../../stores/virtualLibrarianSettingsStore";
import globalAuthStateStore from "../../../../../stores/globalAuthStateStore";
import { VirtualLibrarianService } from "../../../../../services/VirtualLibrarianService";
import { useHookstate } from "@hookstate/core";
import { renderToStaticMarkup } from "react-dom/server";
import {
  getContentType,
  getVideoTitle,
  getDocumentTitle,
} from "../utilities/chatAnswerUtils";
import DOMPurify from "dompurify";
import TabPanel from "../chat/tabPanel";
import VirtualLibrarianResultContentList from "../chat/citationResults";

export default function AdminPlayground() {
  const [selectedButton, setSelectedButton] = useState("single-view");
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [hasQuestion, setHasQuestion] = useState(false);
  const [insights, setInsights] = useState(false);
  const [question, setQuestion] = useState("");
  const [suggestedQuestions, setSuggestedQuestions] = useState([]); // State for suggested questions

  const [selectedTab, setSelectedTab] = useState(0);
  const [sessionId, setSessionId] = useState(() => {
    const savedSessionId = sessionStorage.getItem("virtualLibrarianSessionId");
    return savedSessionId ? savedSessionId : "";
  });
  const [chatItems, setChatItems] = useState(() => {
    const savedChatItems = sessionStorage.getItem("virtualLibrarianChatItems");
    return savedChatItems ? JSON.parse(savedChatItems) : [];
  });
  const [videoCitations, setVideoCitations] = useState(() => {
    const savedVideoCitations = sessionStorage.getItem(
      "virtualLibrarianVideoCitations"
    );
    return savedVideoCitations ? JSON.parse(savedVideoCitations) : [];
  }); // State for video citations
  const [documentCitations, setDocumentCitations] = useState(() => {
    const savedDocumentCitations = sessionStorage.getItem(
      "virtualLibrarianDocumentCitations"
    );
    return savedDocumentCitations ? JSON.parse(savedDocumentCitations) : [];
  }); // State for document citations
  const [citationCount, setCitationCount] = useState(() => {
    const savedCitationCount = sessionStorage.getItem(
      "virtualLibrarianCitationCount"
    );
    return savedCitationCount
      ? JSON.parse(savedCitationCount)
      : documentCitations?.length + videoCitations?.length;
  }); // State for document citations

  const [isThinking, setIsThinking] = useState(false); // New state for thinking indicator

  const settings = useHookstate(virtualLibrarianSettingsStore);
  const authStore = useHookstate(globalAuthStateStore);
  const vlService = React.useMemo(
    () => new VirtualLibrarianService(authStore),
    [authStore]
  );

  useEffect(() => {
    loadSettings();
  }, [settings]);

  useEffect(() => {
    sessionStorage.setItem(
      "virtualLibrarianChatItems",
      JSON.stringify(chatItems)
    );
  }, [chatItems]);

  useEffect(() => {
    sessionStorage.setItem(
      "virtualLibrarianVideoCitations",
      JSON.stringify(videoCitations)
    );
  }, [videoCitations]);

  useEffect(() => {
    sessionStorage.setItem(
      "virtualLibrarianDocumentCitations",
      JSON.stringify(documentCitations)
    );
  }, [documentCitations]);

  useEffect(() => {
    sessionStorage.setItem("virtualLibrarianSessionId", sessionId);
  }, [sessionId]);

  const fetchSuggestedQuestions = async () => {
    try {
      console.log("Fetching suggested questions...");
      // Get recent chat logs to extract questions
      const result = await vlService.getChatLogsById();
      console.log("API result:", result);

      // Check if we have valid data
      if (result && result.Success && result.Data) {
        console.log("Chat logs data:", result.Data);
        const questions = [];

        // Handle both array and single object responses
        const logsArray = Array.isArray(result.Data)
          ? result.Data
          : [result.Data];

        // Process each log entry
        for (const log of logsArray) {
          // Extract question directly from the Question field and timestamp
          if (log.Question && typeof log.Question === "string") {
            console.log("Found question:", log.Question);

            // Count document and video citations
            let documentCount = 0;
            let videoCount = 0;

            // Process citations if they exist
            if (log.Citations) {
              try {
                // Parse citations if they're in string format
                const citationsData =
                  typeof log.Citations === "string"
                    ? JSON.parse(log.Citations)
                    : log.Citations;

                if (citationsData && Array.isArray(citationsData)) {
                  // Count unique content IDs for documents and videos
                  const documentIds = new Set();
                  const videoIds = new Set();

                  citationsData.forEach((citation) => {
                    const docType = citation.type || citation.Type;
                    const contentId =
                      citation.content_id || citation.ContentId || "";

                    if (contentId) {
                      if (docType === "video_section") {
                        videoIds.add(contentId);
                      } else {
                        documentIds.add(contentId);
                      }
                    }
                  });

                  documentCount = documentIds.size;
                  videoCount = videoIds.size;
                }
              } catch (e) {
                console.error("Error parsing citations for question:", e);
              }
            }

            questions.push({
              text: log.Question,
              timestamp: log.CreatedOn || new Date().toISOString(),
              documentCount: documentCount,
              videoCount: videoCount,
            });
          }
        }

        // Sort by timestamp (most recent first)
        questions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // No longer limiting to 3 questions, display all unique questions
        console.log("Final questions to display:", questions);

        if (questions.length > 0) {
          setSuggestedQuestions(questions);
        } else {
          console.log("No questions found in logs");
          setSuggestedQuestions([]);
        }
      } else {
        console.log("No valid data returned from API");
        setSuggestedQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching suggested questions:", error);
      setSuggestedQuestions([]);
    }
  };

  const loadSettings = async () => {
    if (!settings.IS_LOADED.get()) {
      const getSettingsResult = await vlService.getVirtualLibrarianSettings();
      if (getSettingsResult.Success) {
        const searchIndexCountSetting = getSettingsResult.Data.filter(
          (s) => s.setting_name === "SEARCH_INDEX_COUNT"
        )[0];
        if (searchIndexCountSetting) {
          settings.SEARCH_INDEX_COUNT.set({
            ...settings.SEARCH_INDEX_COUNT.get(),
            int_value: searchIndexCountSetting.int_value,
          });
        }

        const returnAllRagResultsSetting = getSettingsResult.Data.filter(
          (s) => s.setting_name === "RETURN_ALL_RAG_RESULTS"
        )[0];
        if (returnAllRagResultsSetting) {
          settings.RETURN_ALL_RAG_RESULTS.set({
            ...settings.RETURN_ALL_RAG_RESULTS.get(),
            bool_value: returnAllRagResultsSetting.bool_value,
          });
        }
        settings.IS_LOADED.set(true);
      }
    }
  };

  const parseAnswerResult = (result) => {
    const citations = [];
    const followupQuestions = [];

    // Extract any follow-up questions that might be in the answer
    let parsedAnswer = result?.Data?.answer?.replace(
      /<<([^>>]+)>>/g,
      (match, content) => {
        followupQuestions.push(content);
        return "";
      }
    );

    // trim any whitespace from the end of the answer after removing follow-up questions
    parsedAnswer = parsedAnswer?.trim();

    const parts = parsedAnswer?.split(/\[([^\]]+)\]/g);

    const fragments = parts?.map((part, index) => {
      if (index % 2 === 0) {
        return part;
      } else {
        let citationIndex;
        if (citations.indexOf(part) !== -1) {
          citationIndex = citations.indexOf(part) + 1;
        } else {
          citations.push(part);
          citationIndex = citations?.length;
        }

        const type = getContentType(part, result.Data.docs_by_id);
        const title =
          type === "video_section"
            ? getVideoTitle(part, result.Data.docs_by_id)
            : getDocumentTitle(part, result.Data.docs_by_id);

        // Determine the target tab based on the content type
        const targetTab = type === "video_section" ? "video" : "document";

        return renderToStaticMarkup(
          <a className="supContainer" title={title}>
            <sup data-docid={part} data-target-tab={targetTab}>
              {citationIndex + citationCount}
            </sup>
          </a>
        );
      }
    });

    const answerHtmlFragments = fragments?.join("");
    setCitationCount(
      (prevCitationCount) =>
        prevCitationCount +
        fragments.filter((fragment) => fragment.includes("supContainer"))
          ?.length
    );

    return {
      answerText: result.Data.answer,
      answerHtml: answerHtmlFragments,
      citations: citations.map((c, index) => {
        return { id: c, index: index + citationCount + 1 };
      }),
      followupQuestions,
      sanitizedAnswerHtml: DOMPurify.sanitize(answerHtmlFragments),
    };
  };

  useEffect(() => {
    fetchSuggestedQuestions();
  }, []);

  const askQuestion = async (questionText) => {
    // Accept question text as argument
    setIsThinking(true); // Set thinking state to true when starting to ask a question

    const askPayload = {
      question: questionText,
      approach: "rrrv",
      overrides: {
        semantic_captions: false,
        semantic_ranker: false,
        top: settings.SEARCH_INDEX_COUNT.int_value.get() ?? 6,
        include_all_results:
          settings.RETURN_ALL_RAG_RESULTS.bool_value.get() ?? false,
      },
      chat_history: chatItems.map((item) => ({
        question: item.question,
        answer: item.answer,
      })),
      chat_session_id:
        sessionId && sessionId?.length === 36 ? sessionId : undefined,
    };

    const result = await vlService.askQuestion(askPayload);
    if (result) {
      setSessionId(result.Data.chat_session_id);
      var answer = parseAnswerResult(result);
      console.log(answer);
      // Use functional update to ensure we have the latest state
      setChatItems((prevChatItems) => [
        ...prevChatItems,
        {
          question: questionText,
          answer: answer.answerText,
          answerHtml: answer.sanitizedAnswerHtml,
        },
      ]);

      // Refresh the previous questions panel after successful ask
      fetchSuggestedQuestions();

      // Process citations to separate video and document types
      if (
        answer.citations &&
        Array.isArray(answer.citations) &&
        result.Data.docs_by_id
      ) {
        const currentVideoCitations = [];
        const currentDocumentCitations = [];

        answer.citations.forEach((citation) => {
          const doc = result.Data.docs_by_id[citation.id];
          if (doc) {
            // Check if the document exists for the guid
            if (doc.type === "video_section") {
              if (!currentVideoCitations.includes((d) => d.id === doc.id)) {
                currentVideoCitations.push({ ...doc, index: citation.index });
              }
            } else if (doc.type === "document_split") {
              if (!currentDocumentCitations.includes((d) => d.id === doc.id)) {
                currentDocumentCitations.push({
                  ...doc,
                  index: citation.index,
                });
              }
            }
          }
        });

        console.log("Filtered Video Citations:", currentVideoCitations);
        setVideoCitations((prevVideoCitations) => [
          ...prevVideoCitations,
          ...currentVideoCitations,
        ]);
        console.log("Filtered Document Citations:", currentDocumentCitations);
        setDocumentCitations((prevDocumentCitations) => [
          ...prevDocumentCitations,
          ...currentDocumentCitations,
        ]);
      } else {
        // Clear citations if the conditions aren't met (e.g., no citations or docs_by_id)
        setVideoCitations([]);
        setDocumentCitations([]);
      }
    }
    setIsThinking(false); // Set thinking state to false when done
    // Consider adding error handling here if the API call fails
  };

  const handleSubmitQuestion = () => {
    if (question.trim()) {
      askQuestion(question);
      setQuestion("");
    }
  };

  const handleSubmitPreviousQuestion = (previousQuestion) => {
    if (previousQuestion.trim()) {
      askQuestion(previousQuestion);
      setQuestion("");
    }
  };

  const handleOnKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitQuestion();
    }
  };

  const handleCitationClick = (event) => {
    const target = event.target;
    if (target.tagName === "SUP" && target.dataset.targetTab) {
      const targetTab = target.dataset.targetTab;
      if (targetTab === "video") {
        setSelectedTab(1); // Assuming AI Results is 0, Video is 1
      } else if (targetTab === "document") {
        // Adjust index based on whether videoCitations tab is present
        const documentTabIndex = videoCitations?.length > 0 ? 2 : 1;
        setSelectedTab(documentTabIndex);
      }
    }
  };

  const handleSetSelectedOrganization = (organization, index) => {
    const newSelectedOrganizations = [...selectedOrganizations];
    newSelectedOrganizations[index] = organization;
    setSelectedOrganizations(newSelectedOrganizations);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  return (
    <div className="playground">
      <PlaygroundHeader
        selectedButton={selectedButton}
        setSelectedButton={setSelectedButton}
        insights={insights}
        setInsights={setInsights}
        videoCitations={videoCitations}
        documentCitations={documentCitations}
        selectedTab={selectedTab}
        handleTabChange={handleTabChange}
      />
      <div className="playground-container">
        <TabPanel
          value={selectedTab}
          index={0}
          className="organization-prompt-container-wrapper"
        >
          <div className="organization-prompt-container">
            <OrganizationPrompt
              selectedOrganization={selectedOrganizations[0]}
              setSelectedOrganization={handleSetSelectedOrganization}
              index={0}
              chatItems={chatItems}
              isThinking={isThinking}
              handleCitationClick={handleCitationClick}
            />
            {selectedButton === "multi-view" && (
              <OrganizationPrompt
                selectedOrganization={selectedOrganizations[1]}
                setSelectedOrganization={handleSetSelectedOrganization}
                index={1}
                chatItems={chatItems}
                isThinking={isThinking}
                handleCitationClick={handleCitationClick}
              />
            )}
          </div>
          <div className="organization-prompt-input-container">
            <textarea
              onChange={(e) => setQuestion(e.target.value)}
              value={question}
              onKeyDown={handleOnKeyDown}
              placeholder="What are examples of coaching questions for new teachers?"
            />
            <button onClick={handleSubmitQuestion} disabled={isThinking}>
              Submit
            </button>
          </div>
          {chatItems?.length === 0 && (
            <div className="initial-prompt">
              <p>Your conversations and results will appear here.</p>
              <p>Enter test prompts below.</p>
            </div>
          )}
        </TabPanel>
        {videoCitations?.length > 0 && (
          <TabPanel
            value={selectedTab}
            index={1}
            className="organization-prompt-container-wrapper"
          >
            <div className="organization-prompt-container">
              <VirtualLibrarianResultContentList citations={videoCitations} />
            </div>
          </TabPanel>
        )}
        {documentCitations?.length > 0 && (
          <TabPanel
            value={selectedTab}
            index={videoCitations?.length > 0 ? 2 : 1}
            className="organization-prompt-container-wrapper"
          >
            <div className="organization-prompt-container">
              <VirtualLibrarianResultContentList
                citations={documentCitations}
              />
            </div>
          </TabPanel>
        )}
        <TestSessions
          suggestedQuestions={chatItems}
          handleSubmitPreviousQuestion={handleSubmitPreviousQuestion}
        />
      </div>
    </div>
  );
}
